/**
 * Cart Upsell Navigation
 * Adds arrow navigation and dot pagination functionality for cart upsell products
 * Works with both Flickity slider mode and native scroll mode
 */

(function() {
  'use strict';

  class CartUpsellNavigation {
    constructor() {
      this.initialized = new Set(); // Track initialized blocks
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupAll());
      } else {
        // DOM already ready, setup immediately
        this.setupAll();
      }

      // Listen for cart drawer open/update
      document.addEventListener('cart:updated', () => {
        setTimeout(() => this.setupAll(), 300);
      });

      // Listen for cart drawer open
      document.addEventListener('drawer:opened', (e) => {
        if (e.detail && e.detail.drawer === 'cart') {
          setTimeout(() => this.setupAll(), 300);
        }
      });
    }

    setupAll() {
      // Setup navigation for all cart upsell blocks
      const widgets = document.querySelectorAll('.cart__widget--products');

      widgets.forEach(widget => {
        const cartUpsellSlider = widget.querySelector('cart-upsell-slider');
        if (cartUpsellSlider) {
          // Check if products are already loaded
          const hasProducts = cartUpsellSlider.querySelector('quick-add-product');

          if (hasProducts) {
            // Products already loaded, setup immediately
            this.setupNavigation(cartUpsellSlider);
          }
        }
      });
    }

    setupNavigation(cartUpsellSliderEl) {
      const widget = cartUpsellSliderEl.closest('.cart__widget--products');
      if (!widget) return;

      // Check if already initialized
      const widgetId = widget.getAttribute('data-widget-id') || 'default';
      if (this.initialized.has(widgetId)) {
        return;
      }
      this.initialized.add(widgetId);

      const prevBtn = widget.querySelector('.cart__widget__arrow--prev');
      const nextBtn = widget.querySelector('.cart__widget__arrow--next');
      const dotsContainer = widget.querySelector('.cart__widget__dots');

      if (!prevBtn || !nextBtn || !dotsContainer) return;

      // Check if slider mode is enabled
      const sliderComponent = cartUpsellSliderEl.querySelector('slider-component');

      if (sliderComponent) {
        // Slider mode with Flickity
        this.setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer);
      } else {
        // Native scroll mode
        this.setupScrollNavigation(cartUpsellSliderEl, prevBtn, nextBtn, dotsContainer);
      }
    }

    setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer) {
      // Wait for Flickity to initialize
      const checkFlickity = () => {
        const flickityEl = sliderComponent.querySelector('.flickity-enabled');

        if (flickityEl && flickityEl.flickityInstance) {
          const flkty = flickityEl.flickityInstance;

          // Create dots
          this.createFlickityDots(flkty, dotsContainer);

          // Update navigation state
          const updateNav = () => {
            prevBtn.disabled = flkty.selectedIndex === 0;
            nextBtn.disabled = flkty.selectedIndex === flkty.slides.length - 1;

            // Update active dot
            const dots = dotsContainer.querySelectorAll('.cart__widget__dot');
            dots.forEach((dot, index) => {
              dot.classList.toggle('is-active', index === flkty.selectedIndex);
            });
          };

          // Arrow click handlers
          prevBtn.addEventListener('click', () => flkty.previous());
          nextBtn.addEventListener('click', () => flkty.next());

          // Listen to Flickity changes
          flkty.on('change', updateNav);
          flkty.on('select', updateNav);

          // Initial update
          updateNav();

        } else {
          // Retry after a short delay
          setTimeout(checkFlickity, 100);
        }
      };

      checkFlickity();
    }

    createFlickityDots(flkty, dotsContainer) {
      dotsContainer.innerHTML = '';

      flkty.slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.classList.add('cart__widget__dot');
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

        if (index === 0) {
          dot.classList.add('is-active');
        }

        dot.addEventListener('click', () => {
          flkty.select(index);
        });

        dotsContainer.appendChild(dot);
      });
    }

    setupScrollNavigation(cartUpsellSliderEl, prevBtn, nextBtn, dotsContainer) {
      // Get all product items
      const productsContainer = cartUpsellSliderEl.querySelector('.cart__widget__products');
      if (!productsContainer) return;

      const items = Array.from(productsContainer.querySelectorAll('quick-add-product'));

      if (items.length === 0) {
        return;
      }

      let currentIndex = 0;

      // Create dots
      const createDots = () => {
        const totalCards = items.length;

        dotsContainer.innerHTML = '';

        // Créer un dot par carte
        for (let i = 0; i < totalCards; i++) {
          const dot = document.createElement('button');
          dot.classList.add('cart__widget__dot');
          dot.setAttribute('type', 'button');
          dot.setAttribute('aria-label', `Go to product ${i + 1}`);

          if (i === 0) {
            dot.classList.add('is-active');
          }

          dot.addEventListener('click', () => {
            goToCard(i);
          });

          dotsContainer.appendChild(dot);
        }

        updateNavigation();
      };

      // Navigate to a specific card by index
      const goToCard = (cardIndex) => {
        if (items.length === 0 || cardIndex < 0 || cardIndex >= items.length) return;

        currentIndex = cardIndex;
        const item = items[cardIndex];

        // Calculer la position de scroll pour cette carte
        const scrollLeft = item.offsetLeft - productsContainer.offsetLeft;

        productsContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });

        updateNavigation();
      };

      // Update button states and active dot
      const updateNavigation = () => {
        const scrollLeft = productsContainer.scrollLeft;
        const scrollMax = productsContainer.scrollWidth - productsContainer.offsetWidth;

        // Disable arrows aux limites
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= scrollMax - 1;

        // Trouver quelle carte est la plus proche de la position actuelle
        let closestIndex = 0;
        let minDistance = Infinity;

        items.forEach((item, index) => {
          const itemLeft = item.offsetLeft - productsContainer.offsetLeft;
          const distance = Math.abs(scrollLeft - itemLeft);

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        currentIndex = closestIndex;

        // Update dots
        const dots = dotsContainer.querySelectorAll('.cart__widget__dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === currentIndex);
        });
      };

      // Handle scroll event to update current index
      let scrollTimeout;
      productsContainer.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          updateNavigation();
        }, 100);
      });

      // Arrow navigation
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) {
          goToCard(currentIndex - 1);
        }
      });

      nextBtn.addEventListener('click', () => {
        if (currentIndex < items.length - 1) {
          goToCard(currentIndex + 1);
        }
      });

      // Handle window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          createDots();
        }, 250);
      });

      // Initialize - wait a bit to ensure layout is stable
      setTimeout(() => {
        createDots();
      }, 200);
    }
  }

  // Define custom element for cart upsell slider
  if (!customElements.get('cart-upsell-slider')) {
    customElements.define('cart-upsell-slider', class extends HTMLElement {
      constructor() {
        super();
      }
    });
  }

  // Initialize when ready
  new CartUpsellNavigation();

})();
